import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Check, Lock, CheckCircle2, Circle, AlertTriangle, Upload, Flag, GitCompareArrows, Award } from "lucide-react";

const ATT_LABELS = {
  present: "Present", late: "Late", authorised_absence: "Authorised absence",
  unauthorised_absence: "Unauthorised absence", sick: "Sick", left_early: "Left early",
};
const RATINGS = [["competent", "Competent"], ["developing", "Developing"], ["serious_concern", "Serious concern"]];
const RATING_STYLES = {
  competent: "bg-emerald-500 text-white border-emerald-500",
  developing: "bg-amber-400 text-black border-amber-400",
  serious_concern: "bg-red-600 text-white border-red-600",
};
const FOUNDATION_LABELS = { competent: "READY FOR EXTERNAL TRAINING", developing: "READY WITH DEVELOPMENT AREAS", serious_concern: "SERIOUS CONCERN" };
const SIM_RATING_LABELS = { strong: "STRONG", suitable: "SUITABLE", requires_review: "REQUIRES REVIEW", not_suitable: "NOT SUITABLE", recommended: "RECOMMENDED" };
const EXT_LABELS = {
  booked: "Course booked", completed: "Result recorded — awaiting approval",
  failed: "Not passed — rebook", converted: "Qualified Development Operator",
};

const inputCls = "bg-white border-steel text-charcoal h-11";

function Card({ title, icon: Icon, children, testid, accent }) {
  return (
    <section className={`bg-white border rounded-md p-5 ${accent ? "border-lomond border-2" : "border-steel"}`} data-testid={testid}>
      <h3 className="font-heading font-bold uppercase tracking-wide text-charcoal text-sm mb-4 flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-lomond" />} {title}
      </h3>
      {children}
    </section>
  );
}

function DayPanel({ cid, day, module: m, rec, isDay10, finalTheory, reload, canEdit }) {
  const [att, setAtt] = useState({ status: "present", arrival: "", departure: "", notes: "" });
  const [tasks, setTasks] = useState({});
  const [pnotes, setPnotes] = useState("");
  const [comments, setComments] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [evFile, setEvFile] = useState(null);
  const [evKind, setEvKind] = useState("photo");
  const [evNote, setEvNote] = useState("");
  const [saving, setSaving] = useState("");

  useEffect(() => {
    setAtt(rec.attendance ? { status: rec.attendance.status, arrival: rec.attendance.arrival || "", departure: rec.attendance.departure || "", notes: rec.attendance.notes || "" } : { status: "present", arrival: "", departure: "", notes: "" });
    setTasks(rec.practical_tasks || {});
    setPnotes(rec.practical_notes || "");
    setComments(rec.instructor_comments || "");
    setOverrideReason("");
  }, [day, rec]);

  const post = async (path, body, key, msg, isForm = false) => {
    setSaving(key);
    try {
      await api.post(path, body, isForm ? { headers: { "Content-Type": "multipart/form-data" } } : undefined);
      toast.success(msg);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const req = isDay10
    ? { "theory content": rec.theory_completed, "foundation theory passed": finalTheory?.status === "pass", "foundation practical assessed": rec.practical_recorded }
    : { "theory content": rec.theory_completed, "quiz passed": rec.quiz_passed, "practical recorded": rec.practical_recorded };
  const missing = Object.entries(req).filter(([, v]) => !v).map(([k]) => k);
  const lastQuiz = rec.quiz_history?.length ? rec.quiz_history[rec.quiz_history.length - 1] : null;

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid={`day-panel-${day}`}>
      <Card title={`Attendance — Day ${day}`} testid={`attendance-card-${day}`}>
        {rec.attendance && (
          <p className="text-xs text-slate-500 mb-3" data-testid={`attendance-recorded-${day}`}>
            Recorded: <span className="font-semibold text-charcoal">{ATT_LABELS[rec.attendance.status]}</span> by {rec.attendance.by_name} — {new Date(rec.attendance.at).toLocaleString("en-GB")}
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <select value={att.status} onChange={(e) => setAtt({ ...att, status: e.target.value })}
            className="col-span-2 h-11 rounded-md border border-steel bg-white px-3 text-sm text-charcoal" data-testid={`attendance-status-${day}`} disabled={!canEdit}>
            {Object.entries(ATT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Input type="time" value={att.arrival} onChange={(e) => setAtt({ ...att, arrival: e.target.value })} className={inputCls} data-testid={`attendance-arrival-${day}`} disabled={!canEdit} />
          <Input type="time" value={att.departure} onChange={(e) => setAtt({ ...att, departure: e.target.value })} className={inputCls} data-testid={`attendance-departure-${day}`} disabled={!canEdit} />
          <Input value={att.notes} onChange={(e) => setAtt({ ...att, notes: e.target.value })} placeholder="Notes" className={`${inputCls} col-span-2`} data-testid={`attendance-notes-${day}`} disabled={!canEdit} />
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => post(`/staff/training/trainees/${cid}/days/${day}/attendance`, att, "att", "Attendance recorded.")}
            className="mt-3 bg-lomond text-black font-bold hover:bg-[#E6B400]" disabled={saving === "att"} data-testid={`attendance-save-${day}`}>
            Save Attendance
          </Button>
        )}
      </Card>

      <Card title={isDay10 ? "Quiz — replaced by Foundation Theory" : `Daily Quiz (pass ${m?.pass_mark ?? 80}%)`} testid={`quiz-card-${day}`}>
        {isDay10 ? (
          <p className="text-sm text-slate-600">Day 10 theory is the Lomond Foundation Theory Assessment — results in the Day 10 section below.</p>
        ) : (
          <div className="space-y-2 text-sm">
            <p className="text-slate-600">Attempts: <span className="font-mono font-bold text-charcoal" data-testid={`quiz-attempts-${day}`}>{rec.quiz_attempts}</span></p>
            <p className="text-slate-600">Best score: <span className="font-mono font-bold text-charcoal" data-testid={`quiz-best-${day}`}>{rec.quiz_best_pct != null ? `${rec.quiz_best_pct}%` : "—"}</span></p>
            <p className="text-slate-600">Status: {rec.quiz_passed
              ? <span className="text-emerald-700 font-bold" data-testid={`quiz-status-${day}`}>PASSED</span>
              : <span className="text-amber-700 font-bold" data-testid={`quiz-status-${day}`}>NOT PASSED YET</span>}</p>
            {lastQuiz && <p className="text-xs text-slate-500">Last attempt {lastQuiz.pct}% — safety-critical errors: <span className={lastQuiz.sc_wrong ? "text-red-600 font-bold" : ""}>{lastQuiz.sc_wrong}</span></p>}
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-steel">
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Trainee reflection</p>
          {rec.reflection ? (
            <div className="space-y-2" data-testid={`reflection-view-${day}`}>
              {[["Learned", rec.reflection.learned], ["Most difficult", rec.reflection.difficult], ["More help with", rec.reflection.help]].map(([l, v]) => (
                <p key={l} className="text-xs text-slate-600"><span className="font-semibold text-charcoal">{l}:</span> {v}</p>
              ))}
            </div>
          ) : <p className="text-xs text-slate-400">Trainee has not reflected yet.</p>}
        </div>
      </Card>

      {!isDay10 && (
        <Card title="Practical Activity Record" icon={CheckCircle2} testid={`practical-card-${day}`}>
          <div className="space-y-2.5">
            {(m?.practical_exercises || []).map((p, i) => (
              <label key={i} className="flex items-start gap-2.5 text-sm text-charcoal cursor-pointer" data-testid={`prac-task-${day}-${i}`}>
                <Checkbox checked={!!tasks[String(i)]} disabled={!canEdit}
                  onCheckedChange={(c) => setTasks({ ...tasks, [String(i)]: !!c })} />
                {p}
              </label>
            ))}
          </div>
          <Textarea value={pnotes} onChange={(e) => setPnotes(e.target.value)} placeholder="Practical notes — what was achieved, standard reached"
            className="bg-white border-steel text-charcoal mt-3" data-testid={`prac-notes-${day}`} disabled={!canEdit} />
          {rec.practical_recorded && <p className="text-xs text-emerald-700 mt-2">Recorded by {rec.practical_by_name} — {new Date(rec.practical_at).toLocaleString("en-GB")}</p>}
          {canEdit && (
            <Button size="sm" onClick={() => post(`/staff/training/trainees/${cid}/days/${day}/practical`, { tasks, notes: pnotes }, "prac", "Practical activity recorded.")}
              className="mt-3 bg-lomond text-black font-bold hover:bg-[#E6B400]" disabled={saving === "prac"} data-testid={`prac-save-${day}`}>
              Record Practical
            </Button>
          )}
        </Card>
      )}

      <Card title="Instructor Comments" icon={CheckCircle2} testid={`comments-card-${day}`}>
        <Textarea value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Comments on today's performance"
          className="bg-white border-steel text-charcoal" data-testid={`comments-input-${day}`} disabled={!canEdit} />
        {canEdit && (
          <Button size="sm" onClick={() => post(`/staff/training/trainees/${cid}/days/${day}/comments`, { comments }, "com", "Comments saved.")}
            className="mt-3 bg-lomond text-black font-bold hover:bg-[#E6B400]" disabled={saving === "com"} data-testid={`comments-save-${day}`}>
            Save Comments
          </Button>
        )}
      </Card>

      <Card title="Training Evidence (optional)" icon={Upload} testid={`evidence-card-${day}`}>
        {rec.evidence?.length > 0 && (
          <ul className="space-y-1.5 mb-3" data-testid={`evidence-list-${day}`}>
            {rec.evidence.map((ev) => (
              <li key={ev.id} className="text-xs flex items-center gap-2">
                <span className="font-mono uppercase text-slate-400 w-20 shrink-0">{ev.kind}</span>
                {ev.url
                  ? <a href={fileUrl(ev.url)} target="_blank" rel="noreferrer" className="text-lomond underline truncate">{ev.name}</a>
                  : <span className="text-slate-600">{ev.name}</span>}
                <span className="text-slate-400 shrink-0">— {ev.by_name}</span>
              </li>
            ))}
          </ul>
        )}
        {canEdit && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <select value={evKind} onChange={(e) => setEvKind(e.target.value)} className="h-11 rounded-md border border-steel bg-white px-2 text-sm text-charcoal" data-testid={`evidence-kind-${day}`}>
                <option value="photo">Photo</option><option value="document">Document</option>
                <option value="assessment_sheet">Assessment sheet</option><option value="note">Note</option>
              </select>
              <Input type="file" onChange={(e) => setEvFile(e.target.files?.[0] || null)} className="bg-white border-steel text-charcoal" data-testid={`evidence-file-${day}`} />
            </div>
            <div className="flex gap-2">
              <Input value={evNote} onChange={(e) => setEvNote(e.target.value)} placeholder="Note / caption (required for notes)" className={inputCls} data-testid={`evidence-note-${day}`} />
              <Button size="sm" disabled={saving === "ev"} data-testid={`evidence-upload-${day}`}
                onClick={() => {
                  const fd = new FormData();
                  if (evFile) fd.append("file", evFile);
                  fd.append("kind", evKind); fd.append("note", evNote);
                  post(`/staff/training/trainees/${cid}/days/${day}/evidence`, fd, "ev", "Evidence added.");
                }}
                className="bg-lomond text-black font-bold hover:bg-[#E6B400] shrink-0">Add</Button>
            </div>
          </div>
        )}
      </Card>

      <Card title="Daily Sign-Off" icon={CheckCircle2} testid={`signoff-card-${day}`} accent={!rec.completed}>
        <div className="space-y-2 mb-4">
          {Object.entries(req).map(([label, ok]) => (
            <p key={label} className="flex items-center gap-2 text-sm" data-testid={`req-${day}-${label.replace(/[^a-z0-9]+/g, "-")}`}>
              {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-300" />}
              <span className={ok ? "text-charcoal" : "text-slate-500"}>{label}</span>
            </p>
          ))}
        </div>
        {rec.completed ? (
          <p className="bg-emerald-100 border border-emerald-300 text-emerald-800 rounded-md px-3 py-2 text-sm font-semibold" data-testid={`signed-off-${day}`}>
            Signed off by {rec.signoff?.by_name} — {new Date(rec.signoff?.at).toLocaleString("en-GB")}
          </p>
        ) : canEdit && (
          <div className="space-y-2">
            {missing.length > 0 && (
              <>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  Incomplete: {missing.join(", ")}. Signing off anyway requires an override reason (audit-logged).
                </p>
                <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Override reason (required to sign off with gaps)"
                  className={inputCls} data-testid={`override-reason-${day}`} />
              </>
            )}
            <Button onClick={() => post(`/staff/training/trainees/${cid}/days/${day}/signoff`, { override_reason: overrideReason }, "sign", `Day ${day} signed off.`)}
              className="bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400]" disabled={saving === "sign"} data-testid={`signoff-btn-${day}`}>
              Sign Off Day {day}
            </Button>
          </div>
        )}
        {rec.overrides?.length > 0 && (
          <div className="mt-4 pt-3 border-t border-steel space-y-1.5" data-testid={`overrides-${day}`}>
            <p className="text-[10px] font-mono uppercase tracking-widest text-red-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Overrides</p>
            {rec.overrides.map((o, i) => (
              <p key={i} className="text-xs text-slate-600">
                <span className="font-semibold">{o.gate === "unlock" ? "Unlocked" : "Sign-off override"}</span> by {o.by_name} — {new Date(o.at).toLocaleString("en-GB")} — "{o.reason}"
                {o.waived?.length ? ` (waived: ${o.waived.join(", ")})` : ""}
              </p>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Week1Card({ cid, review, day5Complete, reload, canEdit }) {
  const blank = { practical_progress: "", safety_concerns: "", comments: "", learning_rate: "steady", strengths: "", development_areas: "", decision: "green", development_issue: "", corrective_action: "", review_date: "", red_reason: "" };
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.post(`/staff/training/trainees/${cid}/week1-review`, form);
      toast.success("Week 1 Review recorded.");
      setEditing(false);
      reload();
    } catch (e) { const msg = apiError(e); setError(msg); toast.error(msg); } finally { setSaving(false); }
  };

  const btnCls = (v) => `flex-1 px-3 py-2.5 rounded-md border text-xs font-bold uppercase tracking-wide transition-all ${
    form.decision === v ? (v === "green" ? "bg-emerald-500 text-white border-emerald-500" : v === "amber" ? "bg-amber-400 text-black border-amber-400" : "bg-red-600 text-white border-red-600")
    : "bg-white text-slate-600 border-steel hover:border-lomond"}`;

  return (
    <Card title="Week 1 Review (end of Day 5)" icon={Flag} testid="week1-card" accent={day5Complete && !review}>
      {review && !editing ? (
        <div className="space-y-3" data-testid="week1-summary">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            {[["Theory average", review.theory_avg != null ? `${review.theory_avg}%` : "—"], ["Learning rate", review.learning_rate], ["Decision", review.decision?.toUpperCase()], ["By", review.by_name]].map(([l, v]) => (
              <div key={l} className="bg-elevated rounded-sm p-3"><p className="font-mono font-bold text-charcoal text-sm">{v}</p><p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">{l}</p></div>
            ))}
          </div>
          {[["Practical progress", review.practical_progress], ["Safety concerns", review.safety_concerns], ["Strengths", review.strengths], ["Development areas", review.development_areas], ["Instructor comments", review.comments]].map(([l, v]) => v && (
            <p key={l} className="text-sm text-slate-600"><span className="font-semibold text-charcoal">{l}:</span> {v}</p>
          ))}
          {review.decision === "amber" && (
            <div className="bg-amber-50 border border-amber-300 rounded-md p-3 text-sm" data-testid="week1-amber-detail">
              <p><span className="font-semibold">Development issue:</span> {review.development_issue}</p>
              <p><span className="font-semibold">Corrective action:</span> {review.corrective_action}</p>
              <p><span className="font-semibold">Review date:</span> {review.review_date}</p>
            </div>
          )}
          {review.decision === "red" && (
            <div className="bg-red-50 border border-red-300 rounded-md p-3 text-sm text-red-700" data-testid="week1-red-detail">
              <p className="font-bold uppercase">Management review required</p>
              <p>{review.red_reason}</p>
            </div>
          )}
          <p className="text-xs text-slate-400">Attendance week 1: {Object.entries(review.attendance || {}).map(([d, s]) => `D${d}: ${s || "—"}`).join(" · ")}</p>
          {canEdit && <Button size="sm" variant="outline" onClick={() => { setForm({ ...blank, ...review }); setEditing(true); }} className="border-steel text-charcoal bg-white" data-testid="week1-edit-btn">Re-record Review</Button>}
        </div>
      ) : !day5Complete ? (
        <p className="text-sm text-slate-500" data-testid="week1-locked">Available once Day 5 is signed off.</p>
      ) : (
        <div className="space-y-3" data-testid="week1-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Textarea value={form.practical_progress} onChange={(e) => set("practical_progress", e.target.value)} placeholder="Practical progress summary" className="bg-white border-steel text-charcoal" data-testid="week1-practical" />
            <Textarea value={form.safety_concerns} onChange={(e) => set("safety_concerns", e.target.value)} placeholder="Safety concerns (or 'None')" className="bg-white border-steel text-charcoal" data-testid="week1-safety" />
            <Textarea value={form.strengths} onChange={(e) => set("strengths", e.target.value)} placeholder="Strengths" className="bg-white border-steel text-charcoal" data-testid="week1-strengths" />
            <Textarea value={form.development_areas} onChange={(e) => set("development_areas", e.target.value)} placeholder="Development areas" className="bg-white border-steel text-charcoal" data-testid="week1-dev" />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-slate-600">Learning rate:</span>
            <div className="flex gap-1.5">
              {["slow", "steady", "fast"].map((lr) => (
                <button key={lr} type="button" onClick={() => set("learning_rate", lr)}
                  className={`px-3 py-1.5 rounded-md border text-xs font-bold uppercase ${form.learning_rate === lr ? "bg-lomond text-black border-lomond" : "border-steel text-slate-500 bg-white"}`}
                  data-testid={`week1-lr-${lr}`}>{lr}</button>
              ))}
            </div>
          </div>
          <Textarea value={form.comments} onChange={(e) => set("comments", e.target.value)} placeholder="Instructor comments" className="bg-white border-steel text-charcoal" data-testid="week1-comments" />
          <div className="flex gap-2" data-testid="week1-decision">
            <button type="button" onClick={() => set("decision", "green")} className={btnCls("green")} data-testid="week1-green">Green — continue</button>
            <button type="button" onClick={() => set("decision", "amber")} className={btnCls("amber")} data-testid="week1-amber">Amber — development</button>
            <button type="button" onClick={() => set("decision", "red")} className={btnCls("red")} data-testid="week1-red">Red — stop / review</button>
          </div>
          {form.decision === "amber" && (
            <div className="grid sm:grid-cols-3 gap-3 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="week1-amber-fields">
              <Input value={form.development_issue} onChange={(e) => set("development_issue", e.target.value)} placeholder="Development issue (required)" className={inputCls} data-testid="week1-issue" />
              <Input value={form.corrective_action} onChange={(e) => set("corrective_action", e.target.value)} placeholder="Corrective action (required)" className={inputCls} data-testid="week1-action" />
              <Input type="date" value={form.review_date} onChange={(e) => set("review_date", e.target.value)} className={inputCls} data-testid="week1-review-date" />
            </div>
          )}
          {form.decision === "red" && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3" data-testid="week1-red-fields">
              <Textarea value={form.red_reason} onChange={(e) => set("red_reason", e.target.value)} placeholder="Reason for RED — triggers management review (required)" className="bg-white border-red-200 text-charcoal" data-testid="week1-red-reason" />
            </div>
          )}
          {error && (
            <p className="bg-red-50 border border-red-300 text-red-700 rounded-md px-3 py-2 text-sm font-semibold" data-testid="week1-error">{error}</p>
          )}
          <Button onClick={save} disabled={saving} className="bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400]" data-testid="week1-save-btn">
            {saving ? "Saving..." : "Record Week 1 Review"}
          </Button>
        </div>
      )}
    </Card>
  );
}

function FoundationPractical({ cid, sections, existing, day10Unlocked, reload, canEdit }) {
  const [vals, setVals] = useState({});
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const init = {};
    sections.forEach(([k]) => { init[k] = existing?.sections?.[k] || { rating: null, comment: "" }; });
    setVals(init);
    setComments(existing?.comments || "");
  }, [existing, sections]);

  const rated = Object.values(vals).filter((v) => v.rating).length;

  const save = async () => {
    if (rated < sections.length) { toast.error(`Rate all ${sections.length} sections (${rated}/${sections.length}).`); return; }
    setSaving(true);
    try {
      const r = await api.post(`/staff/training/trainees/${cid}/final-practical`, { sections: vals, comments });
      toast.success(`Foundation practical saved — overall: ${r.data.overall_result.replace(/_/g, " ")}`);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!day10Unlocked) return <p className="text-sm text-slate-500" data-testid="fp-locked">Unlocks when Day 10 is reached.</p>;

  return (
    <div className="space-y-3" data-testid="foundation-practical-form">
      {existing && (
        <p className={`rounded-md border px-3 py-2 text-sm font-semibold ${
          existing.overall_result === "competent" ? "bg-emerald-50 border-emerald-300 text-emerald-800"
          : existing.overall_result === "developing" ? "bg-amber-50 border-amber-300 text-amber-800"
          : "bg-red-50 border-red-300 text-red-700"}`} data-testid="fp-overall">
          Recorded by {existing.assessor} — overall: {FOUNDATION_LABELS[existing.overall_result]} ({existing.developing_count} developing, {existing.serious_count} serious concern)
        </p>
      )}
      {sections.map(([k, label], i) => (
        <div key={k} className="bg-elevated rounded-md p-3" data-testid={`fp-section-${k}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <span className="text-sm text-charcoal font-medium">{i + 1}. {label}</span>
            <div className="flex gap-1.5">
              {RATINGS.map(([v, l]) => (
                <button key={v} type="button" disabled={!canEdit}
                  onClick={() => setVals({ ...vals, [k]: { ...(vals[k] || {}), rating: v } })}
                  className={`px-3 py-2.5 rounded-md border text-[11px] font-bold uppercase tracking-wide transition-all min-w-24 ${
                    vals[k]?.rating === v ? RATING_STYLES[v] : "bg-white text-slate-600 border-steel hover:border-lomond"
                  }`} data-testid={`fp-${k}-${v}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <Input value={vals[k]?.comment || ""} disabled={!canEdit}
            onChange={(e) => setVals({ ...vals, [k]: { ...(vals[k] || {}), comment: e.target.value } })}
            placeholder="Section comment (optional)" className="bg-white border-steel text-charcoal mt-2 h-9 text-xs" data-testid={`fp-${k}-comment`} />
        </div>
      ))}
      <Textarea value={comments} onChange={(e) => setComments(e.target.value)} disabled={!canEdit}
        placeholder="Overall practical comments" className="bg-white border-steel text-charcoal" data-testid="fp-comments" />
      {canEdit && (
        <Button onClick={save} disabled={saving} className="bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400]" data-testid="fp-save-btn">
          {saving ? "Saving..." : `Save Foundation Practical (${rated}/${sections.length})`}
        </Button>
      )}
    </div>
  );
}

function ExternalCard({ cid, gate, external, reload, isManagement, canEdit }) {
  const blank = { provider: "", course_name: "CPCS A59 360 Excavator (novice course)", start_date: "", end_date: "", cost: "", grant: "", notes: "" };
  const [form, setForm] = useState(blank);
  const [showBook, setShowBook] = useState(false);
  const [result, setResult] = useState("pass");
  const [rnotes, setRnotes] = useState("");
  const [rfile, setRfile] = useState(null);
  const [approveNotes, setApproveNotes] = useState("");
  const [saving, setSaving] = useState("");
  const set = (k, v) => setForm({ ...form, [k]: v });

  const approved = gate?.decision === "approved_external" || !!external;

  const book = async () => {
    setSaving("book");
    try {
      await api.post(`/staff/training/trainees/${cid}/external`, { ...form, cost: parseFloat(form.cost) || 0, grant: parseFloat(form.grant) || 0 });
      toast.success("External course booked — trainee notified and calendar entry created.");
      setShowBook(false);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const submitResult = async () => {
    setSaving("result");
    try {
      const fd = new FormData();
      fd.append("result", result);
      fd.append("notes", rnotes);
      if (rfile) fd.append("file", rfile);
      await api.post(`/staff/training/trainees/${cid}/external/result`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Result recorded and trainee notified.");
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const approve = async () => {
    if (!window.confirm("Approve and convert this trainee to Qualified Development Operator?")) return;
    setSaving("approve");
    try {
      await api.post(`/staff/training/trainees/${cid}/external/approve`, { notes: approveNotes });
      toast.success("Converted to Qualified Development Operator.");
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const net = external ? (Number(external.cost) || 0) - (Number(external.grant) || 0) : 0;

  return (
    <Card title="External 360 Training Provider" icon={Award} testid="external-card" accent={!!external && external.status !== "converted"}>
      {!approved && (
        <p className="text-sm text-slate-500" data-testid="external-locked">Available after the Day 10 provider gate approves this trainee for external training.</p>
      )}

      {approved && (!external || showBook) && canEdit && (
        <div className="space-y-3" data-testid="external-book-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Input value={form.provider} onChange={(e) => set("provider", e.target.value)} placeholder="Training provider (required)" className={inputCls} data-testid="ext-provider" />
            <Input value={form.course_name} onChange={(e) => set("course_name", e.target.value)} placeholder="Course" className={inputCls} data-testid="ext-course" />
            <div className="space-y-1"><label className="text-xs text-slate-500">Start date</label><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className={inputCls} data-testid="ext-start" /></div>
            <div className="space-y-1"><label className="text-xs text-slate-500">End date</label><Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className={inputCls} data-testid="ext-end" /></div>
            <div className="space-y-1"><label className="text-xs text-slate-500">Cost £</label><Input type="number" value={form.cost} onChange={(e) => set("cost", e.target.value)} className={inputCls} data-testid="ext-cost" /></div>
            <div className="space-y-1"><label className="text-xs text-slate-500">Grant £</label><Input type="number" value={form.grant} onChange={(e) => set("grant", e.target.value)} className={inputCls} data-testid="ext-grant" /></div>
          </div>
          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notes (PO number, contact, etc.)" className="bg-white border-steel text-charcoal" data-testid="ext-notes" />
          <Button onClick={book} disabled={saving === "book"} className="bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400]" data-testid="ext-book-btn">
            {saving === "book" ? "Booking..." : external ? "Re-book Course" : "Book Course & Notify Trainee"}
          </Button>
        </div>
      )}

      {approved && !external && !showBook && !canEdit && <p className="text-sm text-slate-500">Not booked yet.</p>}

      {external && !showBook && (
        <div className="space-y-4" data-testid="external-detail">
          <div className="grid sm:grid-cols-3 gap-3">
            {[["Provider", external.provider], ["Course", external.course_name], ["Dates", `${external.start_date} → ${external.end_date}`],
              ["Cost", `£${external.cost}`], ["Grant", `£${external.grant}`], ["Net cost", `£${net}`]].map(([l, v]) => (
              <div key={l} className="bg-elevated rounded-sm p-3">
                <p className="text-sm font-semibold text-charcoal">{v}</p>
                <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">{l}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 font-mono uppercase tracking-widest">
            Status: <span className="text-charcoal font-bold" data-testid="ext-status">{EXT_LABELS[external.status] || external.status}</span>
            {external.booked_by && ` — booked by ${external.booked_by}`}
          </p>
          {external.notes && <p className="text-sm text-slate-600 border-l-2 border-lomond pl-3">{external.notes}</p>}
          {external.certificate_url && (
            <a href={fileUrl(external.certificate_url)} target="_blank" rel="noreferrer" className="text-sm text-lomond underline font-semibold" data-testid="ext-certificate-link">View uploaded certificate / card</a>
          )}
          {external.result && (
            <p className={`rounded-md border px-3 py-2 text-sm font-semibold ${external.result === "pass" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-red-50 border-red-300 text-red-700"}`} data-testid="ext-result">
              Result: {external.result === "pass" ? "PASS" : "NOT PASSED"}{external.result_notes ? ` — ${external.result_notes}` : ""} ({external.result_by})
            </p>
          )}

          {external.status === "booked" && canEdit && (
            <div className="space-y-3 border-t border-steel pt-4" data-testid="ext-result-form">
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Record result</p>
              <div className="flex gap-2">
                {[["pass", "Pass"], ["fail", "Not passed"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setResult(v)}
                    className={`flex-1 px-3 py-2.5 rounded-md border text-xs font-bold uppercase tracking-wide ${
                      result === v ? (v === "pass" ? "bg-emerald-500 text-white border-emerald-500" : "bg-red-600 text-white border-red-600") : "bg-white text-slate-600 border-steel hover:border-lomond"
                    }`} data-testid={`ext-result-${v}`}>{l}</button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input value={rnotes} onChange={(e) => setRnotes(e.target.value)} placeholder="Result notes" className={inputCls} data-testid="ext-result-notes" />
                <Input type="file" accept="image/*,.pdf" onChange={(e) => setRfile(e.target.files?.[0] || null)} className="bg-white border-steel text-charcoal" data-testid="ext-result-file" />
              </div>
              <Button onClick={submitResult} disabled={saving === "result"} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="ext-result-save-btn">
                {saving === "result" ? "Saving..." : "Save Result & Notify Trainee"}
              </Button>
            </div>
          )}

          {external.status === "completed" && isManagement && (
            <div className="space-y-3 border-t border-steel pt-4" data-testid="ext-approve-form">
              <p className="text-sm text-slate-600">Result is a PASS. Approve to convert this trainee to <strong>Qualified Development Operator</strong>.</p>
              <Input value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)} placeholder="Approval notes (certificate verified etc.)" className={inputCls} data-testid="ext-approve-notes" />
              <Button onClick={approve} disabled={saving === "approve"} className="bg-emerald-600 text-white font-heading font-extrabold uppercase hover:bg-emerald-500" data-testid="ext-approve-btn">
                Approve & Convert to Development Operator
              </Button>
            </div>
          )}
          {external.status === "completed" && !isManagement && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="ext-awaiting-approval">Awaiting management approval for conversion.</p>
          )}

          {external.status === "failed" && canEdit && (
            <Button variant="outline" onClick={() => setShowBook(true)} className="border-steel text-charcoal bg-white" data-testid="ext-rebook-btn">Re-book Course</Button>
          )}

          {external.status === "converted" && (
            <p className="bg-emerald-100 border border-emerald-300 text-emerald-800 rounded-md px-4 py-3 text-sm font-semibold" data-testid="ext-converted">
              Converted to Qualified Development Operator{external.approval ? ` by ${external.approval.by_name} — ${new Date(external.approval.at).toLocaleString("en-GB")}` : ""}
              {external.approval?.notes ? ` — ${external.approval.notes}` : ""}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

export default function StaffTrainee() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["management_admin", "instructor"].includes(user?.role);
  const [data, setData] = useState(null);
  const [day, setDay] = useState(null);
  const [gateReason, setGateReason] = useState("");
  const [unlockReason, setUnlockReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/staff/training/trainees/${cid}`)
    .then((r) => { setData(r.data); setDay((d) => d ?? r.data.current_day); })
    .catch(() => toast.error("Could not load trainee"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const moduleFor = useMemo(() => (d) => data?.modules?.find((m) => m.day === d), [data]);

  if (!data) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="staff-trainee-loading" />;
  const { trainee, days, current_day, stats, week1_review, final_theory, final_practical, final_gate, comparison, external, foundation_sections } = data;
  const rec = days[String(day)];

  const gate = async (decision) => {
    if (!gateReason.trim()) { toast.error("A reason / comment is required (audit-logged)."); return; }
    setSaving(true);
    try {
      await api.post(`/staff/training/trainees/${cid}/final-gate`, { decision, reason: gateReason });
      toast.success("Provider gate decision recorded.");
      setGateReason("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const unlockDay = async () => {
    if (!unlockReason.trim()) { toast.error("A reason is required for every override."); return; }
    try {
      await api.post(`/staff/training/trainees/${cid}/days/${day}/override`, { reason: unlockReason });
      toast.success(`Day ${day} unlocked by override.`);
      setUnlockReason("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="staff-trainee-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate("/staff/training")} className="border-steel text-charcoal bg-white" data-testid="back-to-training">
          <ArrowLeft className="h-4 w-4 mr-1" /> Training Camp
        </Button>
        <Link to={`/staff/candidates/${cid}`}>
          <Button variant="outline" size="sm" className="border-steel text-charcoal bg-white" data-testid="open-candidate-profile">Full Profile</Button>
        </Link>
      </div>

      <section className="bg-charcoal text-white rounded-md p-6" data-testid="trainee-header">
        <div className="hazard-stripes h-1.5 w-20 mb-3 rounded-sm" />
        <h1 className="font-heading font-extrabold uppercase tracking-tight text-xl sm:text-2xl" data-testid="trainee-name">{trainee.name}</h1>
        <p className="text-white/60 text-xs font-mono mt-1">{trainee.email} — Day {current_day} of 10{trainee.external_status ? ` — ${trainee.external_status.replace(/_/g, " ")}` : ""}</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4 text-center">
          {[["Theory avg", stats.theory_avg != null ? `${stats.theory_avg}%` : "—", "hd-theory"],
            ["Modules done", `${stats.modules_completed}/10`, "hd-modules"],
            ["Present", stats.attendance.present ?? 0, "hd-attendance"],
            ["Practical", `${stats.practical_done}/10`, "hd-practical"],
            ["Safety concerns", stats.safety_concerns, "hd-safety"]].map(([l, v, tid]) => (
            <div key={l} className="bg-white/10 rounded-sm p-2.5">
              <p className={`font-heading font-black text-xl ${l === "Safety concerns" && v > 0 ? "text-red-400" : "text-[#FFC800]"}`} data-testid={tid}>{v}</p>
              <p className="text-[9px] font-mono uppercase tracking-widest text-white/50">{l}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="flex gap-1.5 flex-wrap" data-testid="day-selector">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((d) => {
          const r = days[String(d)];
          const done = r?.completed;
          const unlocked = r?.unlocked;
          return (
            <button key={d} type="button" onClick={() => setDay(d)}
              className={`h-10 w-10 rounded-md border font-heading font-black text-sm flex items-center justify-center transition-all ${
                day === d ? "ring-2 ring-lomond ring-offset-1" : ""
              } ${done ? "bg-emerald-500 border-emerald-500 text-white" : unlocked ? "bg-white border-steel text-charcoal hover:border-lomond" : "bg-elevated border-steel text-slate-400"}`}
              data-testid={`day-select-${d}`}>
              {done ? <Check className="h-4 w-4" /> : unlocked ? d : <Lock className="h-3.5 w-3.5" />}
            </button>
          );
        })}
      </div>

      {rec && !rec.unlocked && (
        <div className="bg-amber-50 border border-amber-300 rounded-md p-4 flex flex-wrap items-center gap-3" data-testid="day-locked-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800 flex-1">Day {day} is locked — the previous day is not complete yet.</p>
          {canEdit && (
            <div className="flex gap-2 w-full sm:w-auto">
              <Input value={unlockReason} onChange={(e) => setUnlockReason(e.target.value)} placeholder="Override reason (required)" className="bg-white border-amber-300 text-charcoal h-9 text-xs" data-testid="unlock-reason-input" />
              <Button size="sm" onClick={unlockDay} variant="outline" className="border-amber-400 text-amber-800 bg-white h-9 text-xs shrink-0" data-testid="unlock-override-btn">Override Unlock</Button>
            </div>
          )}
        </div>
      )}

      {rec && <DayPanel cid={cid} day={day} module={moduleFor(day)} rec={rec} isDay10={day === 10} finalTheory={final_theory} reload={load} canEdit={canEdit} />}

      <Week1Card cid={cid} review={week1_review} day5Complete={!!days["5"]?.completed} reload={load} canEdit={canEdit} />

      <Card title="Day 10 — Lomond Foundation Theory Assessment" icon={Award} testid="final-theory-card">
        {final_theory ? (
          <div className="space-y-4" data-testid="final-theory-summary">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div className="bg-elevated rounded-sm p-3"><p className="font-heading font-black text-2xl text-charcoal" data-testid="ft-score">{final_theory.score_pct}%</p><p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">Raw score</p></div>
              <div className="bg-elevated rounded-sm p-3"><p className={`font-heading font-black text-2xl ${final_theory.sc_errors ? "text-red-600" : "text-emerald-600"}`} data-testid="ft-sc">{final_theory.sc_errors}</p><p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">Safety-critical errors</p></div>
              <div className="bg-elevated rounded-sm p-3"><p className={`font-heading font-black text-lg uppercase ${final_theory.status === "pass" ? "text-emerald-600" : final_theory.status === "management_review" ? "text-amber-600" : "text-red-600"}`} data-testid="ft-status">{final_theory.status.replace(/_/g, " ")}</p><p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">Overall result</p></div>
              <div className="bg-elevated rounded-sm p-3"><p className="font-heading font-black text-2xl text-charcoal">{final_theory.attempts}</p><p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">Attempts (pass {final_theory.pass_mark}%)</p></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mb-1.5">Areas of strength</p>
                <ul className="text-sm text-slate-600 space-y-1" data-testid="ft-strengths">
                  {Object.entries(final_theory.category_scores || {}).filter(([, v]) => v >= 80).map(([k, v]) => <li key={k}>— {k} ({v}%)</li>)}
                  {!Object.values(final_theory.category_scores || {}).some((v) => v >= 80) && <li>— none at 80%+</li>}
                </ul>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-red-600 mb-1.5">Areas requiring development</p>
                <ul className="text-sm text-slate-600 space-y-1" data-testid="ft-development">
                  {Object.entries(final_theory.category_scores || {}).filter(([, v]) => v < 80).map(([k, v]) => <li key={k}>— {k} ({v}%)</li>)}
                </ul>
              </div>
            </div>
          </div>
        ) : <p className="text-sm text-slate-500" data-testid="ft-not-taken">Foundation theory assessment not taken yet.</p>}
      </Card>

      <Card title="Day 10 — Lomond Foundation Practical Assessment" icon={Award} testid="final-practical-card">
        <FoundationPractical cid={cid} sections={foundation_sections} existing={final_practical} day10Unlocked={!!days["10"]?.unlocked} reload={load} canEdit={canEdit} />
      </Card>

      <Card title="Development Evidence — Selection vs Training" icon={GitCompareArrows} testid="comparison-card">
        <p className="text-xs text-slate-500 mb-4">Pre-training selection results (online assessment, interview, simulator aptitude) alongside training outcomes (camp practical progress and the Day 10 Foundation Practical).</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-elevated rounded-md p-4" data-testid="comparison-pre-training">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Pre-training (selection)</p>
            <div className="space-y-1.5 text-sm">
              <p className="text-slate-600">Online assessment: <span className="font-mono font-bold text-charcoal" data-testid="pre-theory">{comparison.pre_training?.theory_pct != null ? `${comparison.pre_training.theory_pct}%` : "—"}</span></p>
              <p className="text-slate-600">Interview: <span className="font-mono font-bold text-charcoal" data-testid="pre-interview">{comparison.pre_training?.interview_avg != null ? `${comparison.pre_training.interview_avg}/5` : "—"}</span></p>
              <p className="text-slate-600">Simulator aptitude: <span className="font-mono font-bold text-charcoal" data-testid="pre-simulator">{comparison.pre_training?.simulator_avg != null ? `${comparison.pre_training.simulator_avg}/5` : "—"}</span>
                {comparison.pre_training?.simulator_rating && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-lomond">{SIM_RATING_LABELS[comparison.pre_training.simulator_rating] || comparison.pre_training.simulator_rating.replace(/_/g, " ")}</span>}
              </p>
            </div>
          </div>
          <div className="bg-elevated rounded-md p-4" data-testid="comparison-training">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Training Camp outcome</p>
            <p className="text-sm text-slate-600">Practical days completed: <span className="font-mono font-bold text-charcoal" data-testid="training-practical-days">{comparison.training?.practical_done ?? 0}/10</span></p>
            {comparison.training?.foundation ? (
              <>
                <p className="font-heading font-black text-lg text-charcoal mt-2" data-testid="foundation-label">{FOUNDATION_LABELS[comparison.training.foundation.overall_result]}</p>
                <p className="text-xs text-slate-500 mt-1">{comparison.training.foundation.developing_count} developing — {comparison.training.foundation.serious_count} serious concern — {comparison.training.foundation.assessor}, {new Date(comparison.training.foundation.at).toLocaleDateString("en-GB")}</p>
                {comparison.training.foundation.comments && <p className="text-xs text-slate-600 mt-2 border-l-2 border-lomond pl-2.5">{comparison.training.foundation.comments}</p>}
              </>
            ) : <p className="text-sm text-slate-500 mt-2">Foundation practical not recorded yet.</p>}
          </div>
        </div>
      </Card>

      <Card title="Day 10 — Final Provider Gate" icon={Flag} testid="final-gate-card" accent>
        {final_gate ? (
          <div className="space-y-3" data-testid="gate-recorded">
            <p className={`rounded-md border px-4 py-3 font-heading font-bold uppercase ${
              final_gate.decision === "approved_external" ? "bg-emerald-50 border-emerald-300 text-emerald-800"
              : final_gate.decision === "extend_lomond" ? "bg-amber-50 border-amber-300 text-amber-800"
              : "bg-red-50 border-red-300 text-red-700"}`} data-testid="gate-decision">
              {final_gate.decision_label}
            </p>
            <p className="text-sm text-slate-600">{final_gate.reason}</p>
            <p className="text-xs text-slate-400">Recorded by {final_gate.by_name} — {new Date(final_gate.at).toLocaleString("en-GB")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              {[["Theory", final_gate.snapshot?.theory?.score_pct != null ? `${final_gate.snapshot.theory.score_pct}% (${final_gate.snapshot.theory.status?.replace(/_/g, " ")})` : "—"],
                ["Practical", (FOUNDATION_LABELS[final_gate.snapshot?.practical?.overall] || "—")],
                ["Modules", `${final_gate.snapshot?.modules_completed ?? 0}/10`],
                ["Week 1", final_gate.snapshot?.week1_decision?.toUpperCase() || "—"]].map(([l, v]) => (
                <div key={l} className="bg-elevated rounded-sm p-2.5"><p className="font-mono font-bold text-charcoal text-xs">{v}</p><p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-1">{l}</p></div>
              ))}
            </div>
            <p className="text-xs text-slate-500">The trainee remains a TRAINEE until external recognised training is completed and Lomond approves progression.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Combines: Day 10 theory + practical, attendance, Day 1-10 completion, safety record, Week 1 review and instructor comments. Management may override incomplete requirements with a reason (audit-logged).</p>
            <Textarea value={gateReason} onChange={(e) => setGateReason(e.target.value)} placeholder="Reason / summary for this decision (required, audit-logged)"
              className="bg-white border-steel text-charcoal" data-testid="gate-reason-input" disabled={!canEdit} />
            {canEdit && (
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => gate("approved_external")} disabled={saving} className="bg-emerald-600 text-white font-heading font-extrabold uppercase hover:bg-emerald-500" data-testid="gate-approve-btn">Approve for External Training</Button>
                <Button onClick={() => gate("extend_lomond")} disabled={saving} className="bg-amber-400 text-black font-heading font-extrabold uppercase hover:bg-amber-300" data-testid="gate-extend-btn">Extend Lomond Training</Button>
                <Button onClick={() => gate("do_not_progress")} disabled={saving} variant="outline" className="border-red-300 text-red-700 bg-white hover:bg-red-50 font-heading font-extrabold uppercase" data-testid="gate-stop-btn">Do Not Progress / Review</Button>
              </div>
            )}
          </div>
        )}
      </Card>

      <ExternalCard cid={cid} gate={final_gate} external={external} reload={load}
        isManagement={user?.role === "management_admin"} canEdit={canEdit} />
    </div>
  );
}
